import React, { useState, useEffect } from 'react';
import { 
  Lock, 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  Check, 
  AlertCircle,
  RefreshCw,
  Calendar,
  Hash,
  FileText,
  BarChart2
} from 'lucide-react';
import accessCodeService from '../services/accessCodeService';
import { migrateAccessCodes, needsMigration } from '../utils/migrateAccessCodes';

const ADMIN_PASSCODE = 'wisdomain';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  
  const [accessCodes, setAccessCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [editingCode, setEditingCode] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    expires_at: '',
    max_valuations: '',
    is_active: true,
    notes: '',
  });
  
  const [usageCounts, setUsageCounts] = useState({});

  // Check if migration is needed
  const checkMigration = async () => {
    try {
      const needs = await needsMigration();
      if (needs) {
        console.log('Running access code migration...');
        await migrateAccessCodes();
        await loadAccessCodes();
      }
    } catch (error) {
      console.error('Error checking migration:', error);
    }
  };

  // Check authentication on mount
  useEffect(() => {
    const authKey = 'admin_authenticated';
    const authenticated = sessionStorage.getItem(authKey) === 'true';
    setIsAuthenticated(authenticated);
    
    if (authenticated) {
      loadAccessCodes();
      checkMigration();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load access codes
  const loadAccessCodes = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const codes = await accessCodeService.getAllAccessCodes({ includeInactive: true });
      setAccessCodes(codes);
      
      // Load usage counts for each code
      const counts = {};
      for (const code of codes) {
        try {
          const count = await accessCodeService.getValuationUsage(code.code);
          counts[code.id] = count;
        } catch (error) {
          console.error(`Error loading usage for ${code.code}:`, error);
          counts[code.id] = 0;
        }
      }
      setUsageCounts(counts);
    } catch (error) {
      console.error('Error loading access codes:', error);
      setError('Failed to load access codes. Please check your Supabase configuration.');
    } finally {
      setLoading(false);
    }
  };

  // Handle passcode submission
  const handlePasscodeSubmit = (e) => {
    e.preventDefault();
    setPasscodeError('');
    
    if (passcode.toLowerCase() === ADMIN_PASSCODE.toLowerCase()) {
      setIsAuthenticated(true);
      sessionStorage.setItem('admin_authenticated', 'true');
      loadAccessCodes();
      checkMigration();
    } else {
      setPasscodeError('Incorrect passcode. Please try again.');
      setPasscode('');
    }
  };

  // Handle form submission (create or update)
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      if (editingCode) {
        // Update existing code
        await accessCodeService.updateAccessCode(editingCode.id, {
          code: formData.code,
          expires_at: formData.expires_at || null,
          max_valuations: formData.max_valuations ? parseInt(formData.max_valuations, 10) : null,
          is_active: formData.is_active,
          notes: formData.notes || null,
        });
      } else {
        // Create new code
        await accessCodeService.createAccessCode({
          code: formData.code,
          expires_at: formData.expires_at || null,
          max_valuations: formData.max_valuations ? parseInt(formData.max_valuations, 10) : null,
          is_active: formData.is_active,
          notes: formData.notes || null,
        });
      }
      
      // Reset form and reload
      resetForm();
      await loadAccessCodes();
    } catch (error) {
      console.error('Error saving access code:', error);
      setError(error.message || 'Failed to save access code. Code may already exist.');
    }
  };

  // Handle delete
  const handleDelete = async (id, code) => {
    if (!window.confirm(`Are you sure you want to delete access code "${code}"?`)) {
      return;
    }
    
    try {
      await accessCodeService.deleteAccessCode(id);
      await loadAccessCodes();
    } catch (error) {
      console.error('Error deleting access code:', error);
      setError(error.message || 'Failed to delete access code.');
    }
  };

  // Handle edit
  const handleEdit = (code) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      expires_at: code.expires_at || '',
      max_valuations: code.max_valuations || '',
      is_active: code.is_active,
      notes: code.notes || '',
    });
    // Scroll to form when editing
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      code: '',
      expires_at: '',
      max_valuations: '',
      is_active: true,
      notes: '',
    });
    setEditingCode(null);
    setError(null);
  };

  // Check if code is expired
  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    const expirationDate = new Date(expiresAt);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return expirationDate < today;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  // If not authenticated, show passcode form
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-indigo-50/50 relative overflow-hidden flex items-center justify-center p-4">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full -translate-x-48 -translate-y-48"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-indigo-200/25 to-blue-200/25 rounded-full translate-x-40 translate-y-40"></div>
        
        <div className="relative z-10 bg-white rounded-xl shadow-lg border border-gray-100 p-6 sm:p-8 max-w-md w-full mx-4">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl mb-4 shadow-lg">
              <Lock className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Admin Access</h1>
            <p className="text-sm sm:text-base text-gray-600">Enter the passcode to access the admin panel</p>
          </div>
          
          <form onSubmit={handlePasscodeSubmit}>
            <div className="mb-6">
              <label htmlFor="passcode" className="block text-sm font-medium text-gray-700 mb-2">
                Passcode
              </label>
              <input
                type="password"
                id="passcode"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                className={`w-full px-4 py-3 border rounded-md shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 ${
                  passcodeError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter passcode"
                autoFocus
              />
              {passcodeError && (
                <p className="mt-2 text-sm text-red-600">{passcodeError}</p>
              )}
            </div>
            
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300"
            >
              Authenticate
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white pt-16 sm:pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 sm:mb-6 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Access Code Management
                </span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-gray-600">Manage demo access codes and their settings</p>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={loadAccessCodes}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              >
                <RefreshCw className="h-4 w-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>
              <button
                onClick={() => {
                  sessionStorage.removeItem('admin_authenticated');
                  setIsAuthenticated(false);
                }}
                className="px-3 sm:px-4 py-2 text-sm sm:text-base border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors shadow-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6 border border-red-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <p className="text-sm font-medium text-red-800 flex-1">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-600 hover:text-red-800 flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Create/Edit Form - Always Visible */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 sm:mb-6 p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
              {editingCode ? 'Edit Access Code' : 'Create New Access Code'}
            </h2>
            {editingCode && (
              <button
                onClick={resetForm}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                title="Cancel editing"
              >
                <X className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            )}
          </div>
            
            <form onSubmit={handleFormSubmit} className="space-y-4">
              {/* Access Code and Max Valuations - side by side on non-mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-2">
                    Access Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                    placeholder="e.g., CompanyName1"
                    required
                    disabled={!!editingCode}
                  />
                  {editingCode && (
                    <p className="mt-1 text-sm text-gray-500">Code cannot be changed after creation</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="max_valuations" className="block text-sm font-medium text-gray-700 mb-2">
                    <BarChart2 className="inline h-4 w-4 mr-1" />
                    Max Valuations
                  </label>
                  <input
                    type="number"
                    id="max_valuations"
                    value={formData.max_valuations}
                    onChange={(e) => setFormData({ ...formData, max_valuations: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 text-base"
                    placeholder="Leave empty for unlimited"
                    min="1"
                  />
                </div>
              </div>
              
              {/* Expiry Date - after Max Valuations on mobile, separate section on non-mobile */}
              <div>
                <label htmlFor="expires_at" className="block text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Expiry Date
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        const today = new Date();
                        let daysToAdd = 0;
                        
                        switch (e.target.value) {
                          case '1day':
                            daysToAdd = 1;
                            break;
                          case '3days':
                            daysToAdd = 3;
                            break;
                          case '1week':
                            daysToAdd = 7;
                            break;
                          case '2weeks':
                            daysToAdd = 14;
                            break;
                          case '1month':
                            daysToAdd = 30;
                            break;
                          default:
                            return;
                        }
                        
                        const newDate = new Date(today);
                        newDate.setDate(today.getDate() + daysToAdd);
                        const formattedDate = newDate.toISOString().split('T')[0];
                        setFormData({ ...formData, expires_at: formattedDate });
                      }
                      // Reset dropdown to placeholder
                      e.target.value = '';
                    }}
                    className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-md shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 text-base bg-white appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%23666%22%20d%3D%22M6%208L1%203h10z%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[length:12px_12px] bg-[right_1rem_center]"
                  >
                    <option value="">Quick select...</option>
                    <option value="1day">1 day</option>
                    <option value="3days">3 days</option>
                    <option value="1week">1 week</option>
                    <option value="2weeks">2 weeks</option>
                    <option value="1month">1 month</option>
                  </select>
                  <input
                    type="date"
                    id="expires_at"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 text-base [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-50 [&::-webkit-calendar-picker-indicator]:hover:opacity-70 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:ml-2"
                    style={{
                      colorScheme: 'light',
                    }}
                    />
                </div>
              </div>
              
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="inline h-4 w-4 mr-1" />
                  Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-200 text-base"
                  placeholder="Optional notes for internal reference"
                  rows={3}
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Active (inactive codes cannot be used)
                </label>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 border border-transparent text-sm sm:text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300"
                >
                  {editingCode ? 'Update' : 'Create'} Access Code
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>

        {/* Access Codes Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">Access Codes</h2>
          </div>
          
          {loading ? (
            <div className="p-8 sm:p-12 text-center">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-sm sm:text-base text-gray-600">Loading access codes...</p>
            </div>
          ) : accessCodes.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <Hash className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-gray-600 mb-4">No access codes found.</p>
              <button
                onClick={() => {
                  resetForm();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm sm:text-base font-medium rounded-lg shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300"
              >
                <Plus className="h-4 w-4" />
                Create First Access Code
              </button>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block sm:hidden space-y-4">
                {accessCodes.map((code) => {
                  const expired = isExpired(code.expires_at);
                  const usage = usageCounts[code.id] || 0;
                  const limitReached = code.max_valuations && usage >= code.max_valuations;
                  
                  return (
                    <div
                      key={code.id}
                      className={`bg-white border rounded-lg p-4 ${
                        !code.is_active ? 'border-gray-200 bg-gray-50' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center mb-2">
                            <Hash className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-900 font-mono truncate">
                              {code.code}
                            </span>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {expired && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Expired
                              </span>
                            )}
                            {!code.is_active && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                Inactive
                              </span>
                            )}
                            {code.is_active && !expired && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <Check className="h-3 w-3 mr-1" />
                                Active
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 ml-2">
                          <button
                            onClick={() => handleEdit(code)}
                            className="text-blue-600 hover:text-blue-900 p-1"
                            title="Edit"
                          >
                            <Edit className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(code.id, code.code)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Delete"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-gray-500">Expires:</span>
                          <span className={`ml-1 ${
                            expired ? 'text-red-600 font-medium' : 'text-gray-900'
                          }`}>
                            {formatDate(code.expires_at)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Max:</span>
                          <span className="ml-1 text-gray-900">
                            {code.max_valuations ? code.max_valuations : '∞'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Usage:</span>
                          <span className={`ml-1 font-medium ${
                            limitReached ? 'text-red-600' : 'text-gray-900'
                          }`}>
                            {usage} {code.max_valuations ? `/ ${code.max_valuations}` : ''}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Created:</span>
                          <span className="ml-1 text-gray-900">
                            {formatDate(code.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      {code.notes && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <span className="text-xs text-gray-500">Notes:</span>
                          <p className="text-sm text-gray-900 mt-1">{code.notes}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expires
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                          Max Valuations
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Usage
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                          Notes
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                          Created
                        </th>
                        <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {accessCodes.map((code) => {
                        const expired = isExpired(code.expires_at);
                        const usage = usageCounts[code.id] || 0;
                        const limitReached = code.max_valuations && usage >= code.max_valuations;
                        
                        return (
                          <tr key={code.id} className={!code.is_active ? 'bg-gray-50' : ''}>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Hash className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                                <span className="text-xs sm:text-sm font-medium text-gray-900 font-mono">
                                  {code.code}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <span className={`text-xs sm:text-sm ${
                                expired ? 'text-red-600 font-medium' : 'text-gray-900'
                              }`}>
                                {formatDate(code.expires_at)}
                              </span>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                              <span className="text-xs sm:text-sm text-gray-900">
                                {code.max_valuations ? code.max_valuations : '∞'}
                              </span>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <span className={`text-xs sm:text-sm font-medium ${
                                limitReached ? 'text-red-600' : 'text-gray-900'
                              }`}>
                                {usage} {code.max_valuations ? `/ ${code.max_valuations}` : ''}
                              </span>
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                {expired && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Expired
                                  </span>
                                )}
                                {!code.is_active && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Inactive
                                  </span>
                                )}
                                {code.is_active && !expired && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <Check className="h-3 w-3 mr-1" />
                                    Active
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-4 text-xs sm:text-sm text-gray-500 hidden lg:table-cell max-w-xs">
                              {code.notes ? (
                                <span className="truncate block" title={code.notes}>
                                  {code.notes}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">—</span>
                              )}
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 hidden lg:table-cell">
                              {formatDate(code.created_at)}
                            </td>
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleEdit(code)}
                                  className="text-blue-600 hover:text-blue-900 p-1"
                                  title="Edit"
                                >
                                  <Edit className="h-4 w-4 sm:h-5 sm:w-5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(code.id, code.code)}
                                  className="text-red-600 hover:text-red-900 p-1"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;

